import {
	observable,
	computed,
	action,
	runInAction,
	IReactionDisposer,
} from "mobx";

import {
	MainOfferStore,
	getEventSortByFavorite,
	getEventSortBySport,
	getEventSortBySportCategory,
	getEventSortByTournament,
} from "@gp/offer";
import { EventType, LiveStatus } from "@gp/models";
import { EventOffer, SportOffer } from "@gp/offer";
import { ConsoleLogger, LogLevel, Sorter } from "@gp/utility";
import { ISubscriptionRequest } from "@gp/hub";
import { Subscription } from "rxjs";

import betRules from "@betting-rules";

import { BettingTypeSelectorsStore } from "@state/stores/offer/shared/BettingTypeSelectorsStore";
import { LoaderStore } from "@state/stores/common";
import {
	HomePageStoreConstructorsParameters,
	ISportSliderHeaderStore,
	StoreTypeEnum,
} from "./ISportSliderHeaderStore";
import FavoriteStore from "@state/stores/offer/shared/FavoritesStore";
import type { RootStore } from "@state/stores/types";

export class LiveHighlightsViewStore
	extends MainOfferStore
	implements ISportSliderHeaderStore
{
	type = StoreTypeEnum.LiveHighlightsViewStore;

	public rootStore: RootStore;
	private loader: LoaderStore;
	private whenHubStartedDisposer: IReactionDisposer | null;
	private subscription: Subscription | null;
	private favoriteStore: FavoriteStore;
	public route = "top-live";

	//#region observables

	@observable public isStoreInitialized = false;
	@observable public eventsWithErrors: string[] = [];
	@observable public isFetchingData = false;
	@observable public selectedSportId: string;

	//#endregion observables

	//#region  computed

	@computed public get shouldDisplayInitialLoader() {
		return !this.isStoreInitialized;
	}

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	@computed public get isEmpty() {
		return (
			this.sportsList.length === 0 &&
			this.eventsList.length == 0 &&
			!this.isFetchingData &&
			this.isStoreInitialized
		);
	}

	@computed public get eventsList() {
		// this is to make it compatible with new ui components, so all stores use same name for events list
		return this.filteredEvents;
	}

	@computed public get sportsList() {
		return this.events.reduce<ISportSliderHeaderStore["sportsList"][0][]>(
			(acc, e) => {
				const sp = acc.find((s) => s.id === e.sportId);
				if (sp != null) {
					if (sp.count < 15) {
						sp.count++;
					}
					return acc;
				}

				const newSp = this.eventsInSports.sports.find(
					(sp) => sp.id === e.sportId
				);
				if (newSp == null) {
					console.error("Could not find sport in lookups.");
					return acc;
				}

				acc.push({ ...newSp, count: 1 });
				return acc;
			},
			[]
		);
	}

	/** Used internally to sort events. */
	@computed private get filteredEvents(): (EventOffer & {
		sport: SportOffer;
	})[] {
		const filteredEvents = (
			this.events as (EventOffer & { sport: SportOffer })[]
		)
			.filter(
				(event) =>
					!this.eventsWithErrors.includes(event.id) &&
					event.sportId === this.selectedSportId
			)
			.map((e) => {
				if (e.sport == null) {
					e.sport = this.getMappedSport(e);
				}
				return e;
			});
		if (this.favoriteStore.userFavoriteEventsSet.size > 0) {
			return filteredEvents
				.sort(
					Sorter.sort(
						(a1: EventOffer, b1: EventOffer) =>
							getEventSortByFavorite(a1, b1, this.favoriteStore),
						"isTop",
						(a, b) => getEventSortBySport(a, b, this.lookupsStore),
						"startTime",
						(a, b) =>
							getEventSortBySportCategory(
								a,
								b,
								this.lookupsStore
							),
						(a, b) =>
							getEventSortByTournament(a, b, this.lookupsStore),
						"id"
					)
				)
				.slice(0, 15);
		} else {
			return filteredEvents
				.sort(
					Sorter.sort(
						"isTop",
						(a, b) => getEventSortBySport(a, b, this.lookupsStore),
						"startTime",
						(a, b) =>
							getEventSortBySportCategory(
								a,
								b,
								this.lookupsStore
							),
						(a, b) =>
							getEventSortByTournament(a, b, this.lookupsStore),
						"id"
					)
				)
				.slice(0, 15);
		}
	}
	@computed public get eventIdsForEventSwitcher() {
		const list = this.eventsList.map(({ id }) => id);

		return list;
	}

	//#endregion computed

	constructor(config: HomePageStoreConstructorsParameters) {
		super({
			logger: new ConsoleLogger(false, LogLevel.ERROR),
			removeDelay: 10,
			throttle: 4,
			enableThrottling: true,
			bettingTypeSelectorsStore: new BettingTypeSelectorsStore(),
			customConfiguration: betRules,
		});
		this.favoriteStore = config.favoritesStore;
		this.rootStore = config.rootStore;
		this.loader = config.loaderStore;
	}

	//#region data fetching

	@action.bound
	public onInitialize(sportAbrv?: string) {
		this.onSubscriptionDispose();
		this.onDisposeWhenHubStarted();
		this.onFetchData(sportAbrv);
	}

	@action.bound
	private onFetchData(sportAbrv?: string) {
		this.loader.suspend();
		this.isFetchingData = true;

		let subscriptionRequest = this.generateSubscriptionRequestObject();

		this.subscription = this.rootStore.hub
			.getOfferSubscription(subscriptionRequest)
			.subscribe({
				next: (response) => {
					runInAction(() => {
						this.assignOfferData(response);
						if (
							this.eventsInSports.sports.length > 0 &&
							(!this.selectedSportId ||
								!this.eventsInSports.sports.some(
									(sp) => sp.id === this.selectedSportId
								))
						) {
							if (sportAbrv) {
								const sport = this.sportsList.find(
									(sport) => sport.abrv === sportAbrv
								);
								if (sport) {
									this.setSelectedSportId(sport.id);
								} else {
									this.setSelectedSportId(
										this.eventsInSports.sports[0].id
									);
								}
							} else {
								this.setSelectedSportId(
									this.eventsInSports.sports[0].id
								);
							}
						}

						this.onDoneFetching();
					});
				},
				error: (err) => {
					console.error(err);
					this.onDoneFetching();
				},
			});
	}

	private generateSubscriptionRequestObject(): ISubscriptionRequest {
		const bettingTypeConfiguration = this.OfferMapper.getBettingTypes(
			"live",
			betRules
		);

		return {
			subscriptionId: "top-live",
			compress: WEBPACK_OFFER_COMPRESS,
			channels: [
				{
					name: "event",
					filter: {
						eventType: EventType.NORMAL,
						liveStatus: LiveStatus.LIVE,
					},
				},
				{
					name: "betOffer",
					filter: [
						{
							bettingType: {
								abrv: {
									eq: bettingTypeConfiguration.normal,
								},
							},
						},
						{
							bettingType: {
								abrv: {
									eq: bettingTypeConfiguration.marginal,
								},
							},
							isFavorite: true,
						},
					],
				},
			],
		};
	}

	@action.bound
	private onDoneFetching() {
		this.loader.resume();
		this.isFetchingData = false;
		this.isStoreInitialized = true;
	}

	//#endregion data fetching

	//#region disposers

	@action.bound
	public onDispose() {
		this.onSubscriptionDispose();
		this.onDisposeWhenHubStarted();

		this.onReset();
	}

	@action.bound
	public onDisposeOnlySubscription() {
		this.onSubscriptionDispose();
	}

	@action.bound
	private onReset() {
		this.loader.suspend();
		this.isStoreInitialized = false;
		this.isFetchingData = false;
		this.selectedSportId = "";
		this.reset();
	}

	@action.bound
	private onSubscriptionDispose() {
		this.eventsWithErrors = [];
		if (this.subscription) {
			this.subscription.unsubscribe();
			this.subscription = null;
		}
	}

	@action.bound
	private onDisposeWhenHubStarted() {
		if (this.whenHubStartedDisposer) {
			this.whenHubStartedDisposer();
			this.whenHubStartedDisposer = null;
		}
	}

	//#endregion disposers

	//#region actions

	@action.bound
	public registerEventWithError(eventId: string) {
		if (this.eventsWithErrors.includes(eventId)) {
			return;
		}
		this.eventsWithErrors.push(eventId);
	}

	@action.bound
	public setSelectedSportId(newSport: string) {
		this.selectedSportId = newSport;
	}

	//#endregion actions
}
